/**
 * Searching ONE conversation — the bar, per the design.
 *
 * A floating 448px pill that sits under the conversation's tabs: a borderless field with a
 * clear chip, prev/next carets, and a close button. The matches arrive in a panel that
 * FLOATS over the message list — the thread must not move, shrink, or scroll just because
 * someone is looking for something in it — anchored to the whole pill, so it opens exactly
 * as wide as the bar it belongs to.
 *
 * ## The whole pill is "inside"
 *
 * The pill is the anchor — Radix measures `--radix-popover-trigger-width` off it, which is
 * what makes the panel exactly as wide as the bar — and it is also what `onInteractOutside`
 * treats as inside. Radix counts a press on an anchor as an interaction OUTSIDE its
 * content, so without that guard the carets would close the list on their way to the next
 * match.
 *
 * ## Carets step; the list picks
 *
 * A caret moves one match and jumps the thread there with the list LEFT UP: stepping is a
 * walk through the matches, and the reader needs to keep seeing which one they are standing
 * on — the active row highlights and scrolls itself into view as they go. The arrow keys do
 * the identical thing through the same `step`. A click on a row, or Enter, is the other
 * half: that is a choice, not a walk, so it hides the list and leaves the reader with the
 * message they picked.
 *
 * ## Who owns "is the bar showing"
 *
 * Not this component. The toggle lives in the surface's header, a different part of the
 * tree, so the surface mounts this and gets `onClose` back. Mounting IS opening: the
 * field focuses itself, and the surface never has to reach in to focus it.
 *
 * ## Dismissed, not closed — and pressing away does CLOSE
 *
 * Picking a match hides the list but keeps the search — the reader is looking at the
 * message they jumped to, with the bar still on screen holding their term. Focusing or
 * clicking the field brings the same results back without re-running the query.
 *
 * A press anywhere else is the other thing entirely: it closes the whole search, exactly
 * as the close button does. Radix cannot do that job — its `onInteractOutside` only runs
 * while the list is open, and all it does is dismiss the list — so the bar watches the
 * document itself. "Outside" means outside the pill AND outside the portaled panel; the
 * surface's toggle is outside both and still counts as INSIDE, because that button closes
 * the bar on its own and an outside-close would be undone by its click reopening a fresh
 * one.
 *
 * ## The surface routes the jump
 *
 * Choosing a result hands the whole message up, the way `ChannelPinnedStrip` does: a root
 * is focused in the conversation, a reply is focused inside its own thread. Which of
 * those applies depends on the pane the surface is showing, so the surface decides —
 * this component only reports what was chosen.
 */

import { BlockScroll, useDebouncedCallback } from "@birdwatch/ui";
import { cn } from "cn";
import {
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button/button";
import {
  BwCaretDownMdLine,
  BwCaretUpMdLine,
  BwCloseLine,
  BwSearchLine,
} from "@/components/ui/icons";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Typography } from "@/components/ui/typography";
import { formatMessageTimestamp } from "@/libs/date-fns";
import type { ChannelMessage } from "../_api/channel-endpoints";
import { useChannelMessageSearch } from "../_api/use-channel-messages";
import { buildMatchSnippet } from "../_libs/message-search-snippet";
import { ChannelAuthorAvatar, ChannelAuthorName } from "./channel-author";

/** Long enough that a typist does not fire a request per letter, short enough to feel live. */
const SEARCH_DEBOUNCE_MS = 250;

export type ChannelMessageSearchProps = {
  /** The channel to search — the one the surface is showing RIGHT NOW. */
  cid: string;
  /**
   * A result was reached, by click, by Enter, or by a caret. The bar stays; the list stays
   * too unless this was a pick. The surface routes it: a root (`replyToId == null`) is
   * focused in the conversation, a reply is focused inside the thread its `replyToId`
   * names.
   */
  onSelectMessage: (message: ChannelMessage) => void;
  /**
   * The close button, Escape, or a press outside. The surface unmounts the bar; the query
   * dies with it.
   */
  onClose: () => void;
  /**
   * The control that toggles this bar, when the surface has one. A press on it counts as
   * INSIDE: it closes the bar itself, and an outside-close here would be undone a moment
   * later by that same click toggling a freshly mounted bar back open.
   */
  toggleRef?: RefObject<HTMLElement | null>;
  className?: string;
};

export function ChannelMessageSearch({
  cid,
  onSelectMessage,
  onClose,
  toggleRef,
  className,
}: ChannelMessageSearchProps) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // `text` is what is on screen; `term` is what has been asked of the server. They differ
  // for one debounce interval, which is exactly the point.
  const [text, setText] = useState("");
  const [term, setTerm] = useState("");
  // Stepping, picking a result, or clicking away hides the list without abandoning the
  // search — focusing or clicking the field brings it straight back.
  const [dismissed, setDismissed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Mounting IS opening: the surface toggles this into existence and the field takes the
  // caret, so nobody has to reach in and focus it.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Pressing away closes the search outright — the surface unmounts the bar and the term
  // dies with it, the same as the close button. The panel is portaled, so it is a second
  // node to ask rather than a descendant of the pill.
  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (
        barRef.current?.contains(target) ||
        resultsRef.current?.contains(target) ||
        toggleRef?.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    // Capture: a press is outside even when whatever was pressed stops propagation itself.
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [onClose, toggleRef]);

  const commitTerm = useDebouncedCallback((next: string) => {
    setTerm(next);
    setActiveIndex(0);
  }, SEARCH_DEBOUNCE_MS);

  const {
    data: results,
    extra,
    enabled,
    isLoading,
    isError,
  } = useChannelMessageSearch(cid, term);

  // The page IS the whole answer a reader gets, so a full page means "at least this many"
  // rather than a count. The route reports no total — see `useChannelMessageSearch`.
  const hasNextPage = extra?.hasNextPage ?? false;

  const open = enabled && !dismissed;
  // `keepPreviousData` deliberately outlives the query being switched off, so deleting
  // back to one letter leaves the previous term's rows in `data`. Stepping into them would
  // jump the thread into a search the reader already abandoned — hence `enabled`, not just
  // a non-empty array.
  const canStep = enabled && results.length > 0;

  const clear = () => {
    commitTerm.cancel();
    setText("");
    setTerm("");
    setDismissed(false);
    setActiveIndex(0);
    inputRef.current?.focus();
  };

  /** A choice, so the list has done its job and gets out of the way. */
  const pick = (message: ChannelMessage) => {
    setDismissed(true);
    onSelectMessage(message);
  };

  /**
   * Move `delta` matches and jump the thread there, with the list left up: stepping is a
   * walk through the matches, and the reader needs to see which one they are standing on.
   * Results are newest-first, so `-1` is the newer neighbour and `+1` the older one.
   * Wraps, the way a find bar does.
   */
  const step = (delta: number) => {
    if (!canStep) return;
    const next = (activeIndex + delta + results.length) % results.length;
    // `commitTerm` resets the index at debounce time but the new rows land a tick later,
    // so for one render the index can point past the array.
    const message = results[next];
    if (!message) return;
    setActiveIndex(next);
    onSelectMessage(message);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    // Gated on `canStep`, NOT on the panel being open: after a pick has dismissed the
    // list, the arrow keys still have to step.
    if (!canStep) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      step(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      step(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const message = results[activeIndex];
      if (message) pick(message);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) setDismissed(true);
      }}
    >
      {/* The anchor is the WHOLE pill: Radix measures `--radix-popover-trigger-width` off
          this node, so the results open exactly as wide as the bar. */}
      <PopoverAnchor asChild>
        <div
          ref={barRef}
          data-test="channel-search-bar"
          className={cn(
            /* sorted */
            "flex",
            "gap-2 items-center shrink-0",
            "p-1",
            "bg-bg-surface-elevate-low border border-border-dark-low rounded-full shadow-lg",
            className,
          )}
        >
          <div
            className={cn(
              /* sorted */
              "flex",
              "flex-1 gap-2 items-center",
              "min-w-0",
              "px-2 py-1.5",
              "rounded-3xl",
            )}
          >
            <BwSearchLine size={16} className="shrink-0 text-text-dark-low" />
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && results.length > 0
                  ? `${listId}-${activeIndex}`
                  : undefined
              }
              aria-label="Search this conversation"
              placeholder="Search..."
              data-test="channel-search-input"
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setDismissed(false);
                commitTerm(event.target.value);
              }}
              // Coming back to the field re-opens the results it already has. `onClick`
              // as well as `onFocus`, because stepping never moved the caret out — for
              // that reader the field never "receives focus again".
              onFocus={() => setDismissed(false)}
              onClick={() => setDismissed(false)}
              onKeyDown={handleKeyDown}
              className={cn(
                /* sorted */
                "flex-1",
                "min-w-0",
                "text-sm leading-5 font-normal",
                "border-none bg-transparent outline-none",
                "placeholder:text-text-dark-low",
              )}
            />
            {text !== "" && (
              // A raw button, not `Button`: that component's base class forces any unsized
              // svg to `size-5`, and this chip's icon is 16px.
              <button
                type="button"
                aria-label="Clear search"
                data-test="channel-search-clear"
                onClick={clear}
                className={cn(
                  /* sorted */
                  "flex",
                  "shrink-0 items-center",
                  "p-0.5",
                  "rounded-full bg-bg-dark-min cursor-pointer",
                  "hover:bg-bg-dark-min-hover",
                )}
              >
                <BwCloseLine size={16} className="text-text-dark-high" />
              </button>
            )}
          </div>

          {/* `onMouseDown` keeps a caret from taking focus, so the field keeps it: Escape
              and the arrow keys are handled on the input alone, and a focused button would
              strand both. */}
          <div className="flex shrink-0 gap-0.5 items-center">
            <Button
              kind="ghost"
              variant="neutral"
              size="icon-md"
              aria-label="Previous match"
              data-test="channel-search-prev"
              disabled={!canStep}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => step(-1)}
            >
              <BwCaretUpMdLine />
            </Button>
            <Button
              kind="ghost"
              variant="neutral"
              size="icon-md"
              aria-label="Next match"
              data-test="channel-search-next"
              disabled={!canStep}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => step(1)}
            >
              <BwCaretDownMdLine />
            </Button>
          </div>

          <Button
            kind="soft"
            variant="neutral"
            size="icon-md"
            aria-label="Close search"
            data-test="channel-search-done"
            onClick={onClose}
          >
            <BwCloseLine />
          </Button>
        </div>
      </PopoverAnchor>

      <PopoverContent
        ref={resultsRef}
        align="start"
        sideOffset={8}
        collisionPadding={8}
        data-test="channel-search-results"
        // Focus must stay in the field: the popover opens WHILE the user is typing.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        // The bar is the anchor, not the trigger, so Radix reads every press on it —
        // the field, a caret, the clear chip — as "outside" and would close the list
        // under the very controls that drive it.
        onInteractOutside={(event) => {
          if (barRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          onClose();
        }}
        className={cn(
          /* sorted */
          "flex overflow-hidden",
          "flex-col",
          "max-h-96",
          "p-0",
          "bg-bg-surface-elevate-low rounded-3xl",
        )}
      >
        <div className="flex flex-col min-h-0 p-2">
          <div className="shrink-0 px-2 py-1.5">
            <Typography size="sm" className="text-text-dark-low">
              {isLoading
                ? "Searching..."
                : isError
                  ? "Could not search this conversation."
                  : results.length === 0
                    ? `No messages match “${term.trim()}”`
                    : `${results.length}${hasNextPage ? "+" : ""} ${results.length === 1 ? "result" : "results"}`}
            </Typography>
          </div>

          {results.length > 0 && (
            // `BlockScroll` brings its own `overflow-y-auto` and scroll fade — that fade is
            // the design's gradient. `min-h-0` is what lets it shrink inside the column
            // instead of pushing the panel past `max-h-96`.
            <BlockScroll
              id={listId}
              role="listbox"
              className="flex flex-col min-h-0"
            >
              {results.map((message, index) => (
                <ChannelMessageSearchRow
                  key={message.id}
                  id={`${listId}-${index}`}
                  message={message}
                  term={term}
                  isActive={index === activeIndex}
                  // The active row and the one directly above it drop their rule, so the
                  // highlighted row reads as one unbroken block.
                  showDivider={
                    index !== activeIndex &&
                    index !== activeIndex - 1 &&
                    index !== results.length - 1
                  }
                  onSelect={() => pick(message)}
                />
              ))}
            </BlockScroll>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

type ChannelMessageSearchRowProps = {
  id: string;
  message: ChannelMessage;
  term: string;
  isActive: boolean;
  showDivider: boolean;
  onSelect: () => void;
};

function ChannelMessageSearchRow({
  id,
  message,
  term,
  isActive,
  showDivider,
  onSelect,
}: ChannelMessageSearchRowProps) {
  const rowRef = useRef<HTMLDivElement>(null);
  const segments = useMemo(
    () => buildMatchSnippet(message.body, term),
    [message.body, term],
  );

  // Ten caret presses later the list re-opens scrolled to the top, with the row the reader
  // is standing on far below the fold.
  useEffect(() => {
    if (isActive) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [isActive]);

  return (
    <div
      id={id}
      ref={rowRef}
      role="option"
      aria-selected={isActive}
      tabIndex={-1}
      data-test="channel-search-item"
      onClick={onSelect}
      className={cn(
        /* sorted */
        "flex",
        "gap-3 items-center shrink-0",
        "pl-4 pr-3",
        "rounded-2xl cursor-pointer",
        "hover:bg-bg-dark-min",
        isActive && "bg-bg-dark-min",
      )}
    >
      <ChannelAuthorAvatar userId={message.userCreatedId} size="lg" />
      {/* The rule hangs off this stack, not the row, which is what insets it past the
          avatar the way the design draws it. */}
      <div
        className={cn(
          /* sorted */
          "flex",
          "flex-col grow",
          "min-w-0",
          "py-3",
          showDivider && "border-b border-border-dark-min",
        )}
      >
        <div className="flex gap-2 items-center min-w-0">
          <div className="flex-1 min-w-0">
            <ChannelAuthorName
              userId={message.userCreatedId}
              className="truncate text-sm leading-5 font-medium text-text-dark-max"
            />
          </div>
          <span className="shrink-0 pt-0.5 text-xs leading-4 text-text-dark-high">
            {formatMessageTimestamp(message.createdAt)}
          </span>
        </div>
        <p className="truncate text-sm leading-5 text-text-dark-high">
          {segments.map((segment) => (
            <span
              key={`${segment.match}:${segment.text}`}
              className={cn(segment.match && "rounded-sm bg-bg-warning-min")}
            >
              {segment.text}
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}
